import { z } from 'zod';

/**
 * Questions BETA (epopees-et-beta.md §4 et §5 ; architecture §2.9) : chaque question de process ouverte
 * est posée en pop-up « BETA test » au moment où la situation se présente, et sa réponse devient un réglage appris.
 * La valeur par défaut est la plus prudente tant que personne n'a répondu.
 */

export interface QuestionBeta {
  id: string;
  cle: string;
  question: string;
  declencheur: { type: 'ecran' | 'evenement'; cle: string };
  audience: 'dirigeante' | 'assistante' | 'admin';
  options: readonly { code: string; libelle: string }[];
  defaut: string | null;
}

const q = (
  n: number,
  cle: string,
  question: string,
  declencheur: QuestionBeta['declencheur'],
  options: [string, string][],
  defaut: string | null,
  audience: QuestionBeta['audience'] = 'dirigeante',
) =>
  ({
    id: `BETA-${String(n).padStart(2, '0')}`,
    cle,
    question,
    declencheur,
    audience,
    options: options.map(([code, libelle]) => ({ code, libelle })),
    defaut,
  }) as const;

export const QUESTIONS_BETA = [
  q(
    1,
    'courrier_annonce.canal',
    'Comment envoyer le courrier d’annonce aux héritiers de ce dossier ?',
    { type: 'evenement', cle: 'tableau_vivants.termine' },
    [
      ['poste_en_plus', 'Toujours par la poste en plus'],
      ['numerique_puis_poste', 'E-mail et WhatsApp d’abord, courrier si pas de réponse sous 15 jours'],
      ['par_heritier', 'Au choix par héritier'],
    ],
    'numerique_puis_poste',
  ),
  q(
    2,
    'seuil_moins_6_ans',
    'Pour un décès de plus de 6 ans, faut-il préparer une déclaration de succession ?',
    { type: 'evenement', cle: 'person.added.deces_ancien' },
    [
      ['non', 'Non'],
      ['oui_toujours', 'Oui, toujours'],
      ['au_cas_par_cas', 'Au cas par cas'],
    ],
    'au_cas_par_cas',
  ),
  q(
    3,
    'brigitte_whatsapp_isabelle',
    'Brigitte peut-elle envoyer elle-même depuis le WhatsApp d’Isabelle ?',
    { type: 'evenement', cle: 'message.programme.numero_isabelle' },
    [
      ['librement', 'Oui, librement'],
      ['avec_validation', 'Oui, avec la validation d’Isabelle'],
      ['non', 'Non'],
    ],
    'avec_validation',
  ),
  q(
    4,
    'institutionnels.regle',
    'Que doit voir ce client institutionnel de son dossier ?',
    { type: 'evenement', cle: 'dossier.rattache_institution' },
    [
      ['rien', 'Rien'],
      ['grandes_etapes', 'Les grandes étapes'],
      ['etapes_et_blocages', 'Les étapes et les blocages, sans données des familles'],
      ['tout_sauf_sensible', 'Tout sauf le sensible'],
    ],
    'rien',
  ),
  q(
    5,
    'cloture.acces_externe',
    'Après la clôture, qui garde l’accès, et combien de temps ?',
    { type: 'evenement', cle: 'dossier.closed' },
    [
      ['une_semaine', 'Une semaine, puis l’équipe MAAT seulement'],
      ['quatre_mois', 'Quatre mois, puis l’équipe MAAT seulement'],
      ['plus_long', 'Plus longtemps'],
      ['autre', 'Autre règle'],
    ],
    'une_semaine',
  ),
  q(
    6,
    'volume.dossiers',
    'Combien de dossiers actifs et d’héritiers avez-vous en moyenne ?',
    { type: 'ecran', cle: 'D01' },
    [
      ['moins_20', 'Moins de 20'],
      ['de_20_a_50', '20 à 50'],
      ['plus_50', 'Plus de 50'],
    ],
    null,
  ),
  q(
    7,
    'encaissement_notaire',
    'Qui encaisse l’argent destiné au notaire ?',
    { type: 'evenement', cle: 'collecte.frais_acte.premiere' },
    [
      ['etude_directement', 'Directement l’étude'],
      ['cabinet_puis_reversement', 'Le cabinet, puis reversement (à valider)'],
      ['ne_sait_pas', 'Je ne sais pas'],
    ],
    'etude_directement',
  ),
  q(
    8,
    'estimation.visible_client',
    'Peut-on montrer aux clients l’estimation ?',
    { type: 'ecran', cle: 'D04c.afficher_au_client' },
    [
      ['jamais', 'Jamais'],
      ['apres_titre', 'Après le titre trouvé'],
      ['au_cas_par_cas', 'Au cas par cas'],
    ],
    'jamais',
  ),
  q(
    9,
    'video_isabelle',
    'Quelle vidéo d’Isabelle joindre au message d’accueil ?',
    { type: 'evenement', cle: 'message.A1.premiere_video' },
    [
      ['generique', 'Une vidéo générique'],
      ['par_situation', 'Une vidéo par situation'],
      ['pas_de_video', 'Pas de vidéo'],
    ],
    'pas_de_video',
  ),
  q(
    10,
    'attestation.deux_tiers',
    'La signature de l’attestation immobilière aux 2/3 en cas de silence est-elle votre pratique à généraliser, et avec quel notaire ?',
    { type: 'evenement', cle: 'attestation.bloquee_30_jours' },
    [
      ['generaliser', 'Oui, à généraliser'],
      ['au_cas_par_cas', 'Au cas par cas'],
      ['non', 'Non'],
    ],
    'au_cas_par_cas',
  ),
  q(
    11,
    'contact_delegue.regle',
    'Quand un héritier n’a aucun téléphone, le contact délégué tenu par le cabinet est-il la règle, et sous quelle forme de procuration ?',
    { type: 'evenement', cle: 'person.sans_telephone' },
    [
      ['regle_procuration_ecrite', 'Oui, avec une procuration écrite'],
      ['au_cas_par_cas', 'Au cas par cas'],
      ['non', 'Non'],
    ],
    'au_cas_par_cas',
  ),
  q(
    12,
    'conjoint.option_moment',
    'Faut-il demander au conjoint survivant d’exercer son option (usufruit ou 1/4) dès l’ouverture, ou attendre le notaire ?',
    { type: 'evenement', cle: 'conjoint_survivant.enfants_communs' },
    [
      ['des_ouverture', 'Dès l’ouverture'],
      ['attendre_notaire', 'Attendre le notaire'],
    ],
    'attendre_notaire',
  ),
  q(
    13,
    'langues.messages',
    'Dans quelles langues les messages doivent-ils pouvoir partir (créole, anglais) ?',
    { type: 'evenement', cle: 'person.residence_hors_france' },
    [
      ['francais', 'Français seulement'],
      ['francais_creole', 'Français et créole'],
      ['francais_creole_anglais', 'Français, créole et anglais'],
    ],
    'francais',
  ),
  // PARITE-5 (RFC-P5-001) : BETA-25 (les numéros 14 à 24 sont ceux de l'annexe A bis d'epopees-et-beta.md, non encore au
  // catalogue ; le format BETA-NN de BetaQuestionView est gardé). La règle du remboursement en foncier d'une avance entre héritiers manque aux sources (RG-28 ne la fixe
  // pas) ; posée à Isabelle à l'ouverture de la carte des compensations (D08), défaut le plus prudent.
  {
    id: 'BETA-25',
    cle: 'compensation.valeur_foncier',
    question:
      'Quand un héritier rembourse en foncier l’avance d’un autre héritier, quelle valeur retient-on pour le foncier, et qui la valide ?',
    declencheur: { type: 'ecran', cle: 'D08.compensations' },
    audience: 'dirigeante',
    options: [
      { code: 'estimation_cabinet', libelle: 'L’estimation du cabinet, acceptée par écrit par les héritiers concernés' },
      { code: 'expert', libelle: 'Une valeur fixée par un expert (géomètre, agent immobilier ou notaire)' },
      { code: 'au_cas_par_cas', libelle: 'Au cas par cas, décidé avec le notaire' },
    ],
    defaut: 'au_cas_par_cas',
  },
  // Lot 4, point 8 (arbitrage du collège, 26/10) : posées au bon moment par l'écran concerné, jamais en liste. Numérotées 14 et 15 sur
  // defauts/lot4, renumérotées 26 et 27 à l'intégration (14 à 24 : annexe A bis ; 25 : parité 5).
  q(
    26,
    'etude.acces_notaire_signataire',
    'À la création d’une étude, le notaire signataire reçoit-il aussi l’accès, en plus du premier clerc ?',
    { type: 'ecran', cle: 'D10.etude_creee' },
    [
      ['premier_clerc', 'Non, le premier clerc seulement'],
      ['clerc_et_notaire', 'Oui, le premier clerc et le notaire signataire'],
      ['au_cas_par_cas', 'Au cas par cas, à chaque étude'],
    ],
    'premier_clerc',
    'admin',
  ),
  q(
    27,
    'titre.ajuster_valeur_terrain',
    'Faut-il ajuster la valeur du terrain selon que le titre de propriété est trouvé ou non ?',
    { type: 'ecran', cle: 'D04c.valeur_terrain' },
    [
      ['aucun_ajustement', 'Non, la même valeur dans les deux cas'],
      ['decote_sans_titre', 'Oui, une décote tant que le titre n’est pas trouvé'],
      ['au_cas_par_cas', 'Au cas par cas'],
    ],
    'aucun_ajustement',
  ),
  // Arbitrage du team-lead (26/10) : Brigitte garde les montants et émet (T-L37-VUE-03) ; la question est posée à Isabelle à
  // l'ouverture de l'administration (D10), jamais sur D08 où la carte des compensations pose déjà BETA-25 (une seule pop-up BETA à
  // la fois, régression du 27/10). Défaut « oui » : le comportement en place.
  q(
    28,
    'facturation.assistante_emet_et_voit_montants',
    'Brigitte peut-elle émettre des factures et voir les montants ?',
    { type: 'ecran', cle: 'D10' },
    [
      ['oui', 'Oui, elle émet les factures et voit les montants'],
      ['montants_sans_emettre', 'Elle voit les montants, mais seule Isabelle émet'],
      ['non', 'Non, ni montants ni émission'],
    ],
    'oui',
  ),
] as const satisfies readonly QuestionBeta[];

export type CleBeta = (typeof QUESTIONS_BETA)[number]['cle'];

export function questionBeta(cle: CleBeta): QuestionBeta {
  const x = QUESTIONS_BETA.find((y) => y.cle === cle);
  if (!x) throw new Error(`Question BETA inconnue : ${cle}`);
  return x;
}

/** Schéma de réponse d'une question : une option connue, un commentaire libre facultatif. */
export function schemaReponseBeta(cle: CleBeta) {
  const codes = questionBeta(cle).options.map((o) => o.code) as [string, ...string[]];
  return z.object({ option: z.enum(codes), commentaire: z.string().optional() });
}
